import { KBarProvider, useKBar, useRegisterActions } from "kbar";
import { type PropsWithChildren, useMemo } from "react";

import { useAiAdminPaths } from "metabase/admin/ai/use-ai-admin-paths";
import { useEmbeddingAdminPaths } from "metabase/admin/embedding/hooks";
import { usePeopleAdminPaths } from "metabase/admin/people/hooks/use-people-admin-paths";
import { getPerformanceAdminPaths } from "metabase/admin/performance/constants/complex";
import { useDataStudioPaths } from "metabase/common/data-studio/hooks/use-data-studio-paths";
import { canAccessDataStudio } from "metabase/common/data-studio/selectors";
import type { PaletteAction } from "metabase/palette/types";
import { PLUGIN_CACHING } from "metabase/plugins";
import { useSelector } from "metabase/redux";
import { getAdminPaths } from "metabase/selectors/admin";
import { getUserIsAdmin } from "metabase/selectors/user";

export const AppKBarProvider = ({ children }: PropsWithChildren) => (
  <KBarProvider>
    <AppPaletteActions />
    {children}
  </KBarProvider>
);

const AppPaletteActions = () => {
  const isAdmin = useSelector(getUserIsAdmin);
  const adminPaths = useSelector(getAdminPaths);
  const embeddingAdminPaths = useEmbeddingAdminPaths();
  const peopleAdminPaths = usePeopleAdminPaths();
  const aiAdminPaths = useAiAdminPaths();
  const hasDataStudioAccess = useSelector(canAccessDataStudio);
  const dataStudioPaths = useDataStudioPaths();
  const { searchQuery } = useKBar((state) => ({
    searchQuery: state.searchQuery,
  }));
  const hasQuery = searchQuery.trim().length > 0;

  const adminActions = useMemo<PaletteAction[]>(() => {
    const adminSubpaths = isAdmin
      ? [
          ...getPerformanceAdminPaths(PLUGIN_CACHING.getTabMetadata()),
          ...embeddingAdminPaths,
          ...peopleAdminPaths,
          ...aiAdminPaths,
        ]
      : [];

    return [...adminPaths, ...adminSubpaths].map((adminPath) => ({
      id: `admin-page-${adminPath.key}`,
      name: adminPath.name,
      icon: "gear",
      perform: () => {},
      section: "admin",
      extra: {
        href: adminPath.path,
      },
    }));
  }, [
    isAdmin,
    adminPaths,
    embeddingAdminPaths,
    peopleAdminPaths,
    aiAdminPaths,
  ]);

  const dataStudioActions = useMemo<PaletteAction[]>(() => {
    if (!hasDataStudioAccess) {
      return [];
    }

    return dataStudioPaths.map((dataStudioPath) => ({
      id: `data-studio-page-${dataStudioPath.key}`,
      name: dataStudioPath.name,
      icon: "table",
      perform: () => {},
      section: "basic",
      extra: {
        href: dataStudioPath.path,
      },
    }));
  }, [hasDataStudioAccess, dataStudioPaths]);

  useRegisterActions(hasQuery ? adminActions : [], [adminActions, hasQuery]);
  useRegisterActions(hasQuery ? dataStudioActions : [], [
    dataStudioActions,
    hasQuery,
  ]);

  return null;
};
